import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { drawLeafShape, type LeafKind } from "./leafShapes";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const MOBILE_LEAVES_RATIO = 0.5;
const LEAVES_COUNT = 50;
const GLOBAL_ALPHA = 0.6;
const STREAKS_COUNT = 16;
const WIND_ANGLE_DEGREES = 25;
const WIND_STRENGTH = 4;
const FIRST_GUST_DELAY_FRAMES = 60;
const GUST_INTERVAL_MIN_FRAMES = 480;
const GUST_INTERVAL_RANGE_FRAMES = 240;
const MAPLE_SHARE = 0.6;

export type FallingLeavesAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type LeafParticle = {
  x: number;
  y: number;
  radius: number;
  kind: LeafKind;
  fallSpeed: number;
  swayAmplitude: number;
  swaySpeed: number;
  swayPhase: number;
  rotation: number;
  spin: number;
  flipSpeed: number;
  flipPhase: number;
  windFactor: number;
  depth: number;
};

type WindStreak = {
  x: number;
  y: number;
  length: number;
  speed: number;
  bow: number;
};

type GustState = {
  wind: number;
  target: number;
  framesUntilGust: number;
  framesLeft: number;
  duration: number;
  peak: number;
  side: number;
  baseDirectionX: number;
  baseDirectionY: number;
  directionX: number;
  directionY: number;
  curve: number;
};

export const FallingLeavesAnimation: FC<FallingLeavesAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function leafColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
        case "light":
          return `hsla(${hue}, 55%, 58%, ${alpha * 0.85})`;
        case "darkdull":
          return `hsla(0, 0%, 82%, ${alpha * 0.78})`;
        case "lightdull":
          return `hsla(0, 0%, 38%, ${alpha * 0.72})`;
        default:
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    const gust = mkGustState();
    let streaks: WindStreak[] = [];

    const leavesCount = isMobile ? Math.round(LEAVES_COUNT * MOBILE_LEAVES_RATIO) : LEAVES_COUNT;
    const leaves: LeafParticle[] = Array.from({ length: leavesCount }, () =>
      mkLeaf(canvasEl.width, canvasEl.height, false)
    );

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, [...leaves, ...streaks]);
    }

    window.addEventListener("resize", onResize);

    let frame = 0;

    function drawLeaf(leaf: LeafParticle, alpha: number): void {
      context.save();
      context.translate(leaf.x, leaf.y);
      context.rotate(leaf.rotation);
      context.scale(1, Math.cos(frame * leaf.flipSpeed + leaf.flipPhase));

      drawLeafShape(context, leafColor, leaf.kind, leaf.radius, alpha);
      context.restore();
    }

    function drawStreaks(frameSteps: number): void {
      if (gust.wind < 0.5 || streaks.length === 0) {
        return;
      }
      const width = canvasEl.width;
      const height = canvasEl.height;
      const margin = 120;
      const norm = Math.hypot(gust.directionX, gust.directionY) || 1;
      const unitX = gust.directionX / norm;
      const unitY = gust.directionY / norm;
      const perpendicularX = -unitY;
      const perpendicularY = unitX;

      context.strokeStyle = leafColor(Math.min(1, (gust.wind - 0.5) / 3) * 0.4);
      context.lineWidth = 1.2;
      context.lineCap = "round";
      for (const streak of streaks) {
        streak.x += unitX * streak.speed * gust.wind * 0.7 * frameSteps;
        streak.y += unitY * streak.speed * gust.wind * 0.7 * frameSteps;
        wrapStreak(streak, width, height, margin);
        const endX = streak.x - unitX * streak.length;
        const endY = streak.y - unitY * streak.length;
        const controlX = (streak.x + endX) / 2 + perpendicularX * streak.bow * streak.length;
        const controlY = (streak.y + endY) / 2 + perpendicularY * streak.bow * streak.length;
        context.beginPath();
        context.moveTo(streak.x, streak.y);
        context.quadraticCurveTo(controlX, controlY, endX, endY);
        context.stroke();
      }
    }

    const isDarkDull = theme === "darkdull";

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      frame += frameSteps;
      const width = canvasEl.width;
      const height = canvasEl.height;

      const gustChange = stepGustBy(gust, frameSteps);
      if (gustChange === "started") {
        streaks = mkStreaks(width, height);
      } else if (gustChange === "ended") {
        streaks = [];
      }
      drawStreaks(frameSteps);

      const windX = gust.wind * gust.directionX;
      const windY = gust.wind * gust.directionY;
      for (const leaf of leaves) {
        leaf.x +=
          (Math.sin(frame * leaf.swaySpeed + leaf.swayPhase) * leaf.swayAmplitude * 0.3 + windX * leaf.windFactor) *
          frameSteps;
        leaf.y += (leaf.fallSpeed + windY * leaf.windFactor) * frameSteps;
        leaf.rotation += (leaf.spin + windX * 0.012 * leaf.windFactor) * frameSteps;

        const margin = leaf.radius * 2;
        if (leaf.y - margin > height || leaf.y < -height * 1.2) {
          Object.assign(leaf, mkLeaf(width, height, true));
          continue;
        }
        if (leaf.x < -margin * 3) {
          leaf.x = width + margin * 2;
        } else if (leaf.x > width + margin * 3) {
          leaf.x = -margin * 2;
        }

        drawLeaf(leaf, resolveLeafAlpha(leaf.depth, isDarkDull));
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};

function mkGustState(): GustState {
  return {
    wind: 0,
    target: 0,
    framesUntilGust: FIRST_GUST_DELAY_FRAMES,
    framesLeft: 0,
    duration: 240,
    peak: 0,
    side: 1,
    baseDirectionX: 0.9,
    baseDirectionY: -0.42,
    directionX: 0.9,
    directionY: -0.42,
    curve: 0,
  };
}

type GustChange = "started" | "ended" | "none";

function stepGustBy(gust: GustState, steps: number): GustChange {
  let change: GustChange = "none";
  for (let step = 0; step < steps; step++) {
    const stepChange = stepGust(gust);
    if (stepChange !== "none") {
      change = stepChange;
    }
  }
  return change;
}

function stepGust(gust: GustState): GustChange {
  let change: GustChange = "none";

  if (gust.framesLeft > 0) {
    gust.framesLeft--;
    const progress = 1 - gust.framesLeft / gust.duration;
    gust.target = gust.peak * Math.sin(Math.PI * progress);
    const curl = gust.curve * (progress * 2 - 1);
    const cos = Math.cos(curl);
    const sin = Math.sin(curl);
    gust.directionX = gust.baseDirectionX * cos - gust.baseDirectionY * sin;
    gust.directionY = gust.baseDirectionX * sin + gust.baseDirectionY * cos;
    if (gust.framesLeft === 0) {
      gust.framesUntilGust = GUST_INTERVAL_MIN_FRAMES + Math.floor(Math.random() * GUST_INTERVAL_RANGE_FRAMES);
      change = "ended";
    }
  } else {
    gust.target = 0;
    gust.directionX = gust.baseDirectionX;
    gust.directionY = gust.baseDirectionY;
    gust.framesUntilGust--;
    if (gust.framesUntilGust <= 0) {
      gust.side = -gust.side;
      const angle = (WIND_ANGLE_DEGREES * Math.PI) / 180;
      gust.baseDirectionX = gust.side * Math.cos(angle);
      gust.baseDirectionY = -Math.sin(angle);
      gust.curve = (Math.random() < 0.5 ? -1 : 1) * (0.18 + Math.random() * 0.16);
      gust.peak = WIND_STRENGTH * (0.9 + Math.random() * 0.2);
      gust.duration = 220 + Math.floor(Math.random() * 180);
      gust.framesLeft = gust.duration;
      change = "started";
    }
  }

  gust.wind += (gust.target - gust.wind) * 0.05;
  return change;
}

function mkStreaks(width: number, height: number): WindStreak[] {
  return Array.from({ length: STREAKS_COUNT }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    length: 45 + Math.random() * 90,
    speed: 2 + Math.random() * 2.5,
    bow: (Math.random() - 0.5) * 0.5,
  }));
}

function wrapStreak(streak: WindStreak, width: number, height: number, margin: number): void {
  if (streak.x < -margin) {
    streak.x = width + margin;
  } else if (streak.x > width + margin) {
    streak.x = -margin;
  }
  if (streak.y < -margin) {
    streak.y = height + margin;
  } else if (streak.y > height + margin) {
    streak.y = -margin;
  }
}

function mkLeaf(width: number, height: number, spawnAboveViewport: boolean): LeafParticle {
  const kind: LeafKind = Math.random() < MAPLE_SHARE ? "maple" : "ovate";
  const radius = (8 + Math.random() * 9) * (kind === "maple" ? 1.44 : 1);
  return {
    x: Math.random() * width,
    y: spawnAboveViewport ? -radius - Math.random() * height * 0.4 : Math.random() * height,
    radius,
    kind,
    fallSpeed: 0.35 + Math.random() * 0.4,
    swayAmplitude: 0.5 + Math.random() * 1.0,
    swaySpeed: 0.01 + Math.random() * 0.02,
    swayPhase: Math.random() * Math.PI * 2,
    rotation: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.02,
    flipSpeed: 0.02 + Math.random() * 0.03,
    flipPhase: Math.random() * Math.PI * 2,
    windFactor: 0.75 + Math.random() * 0.6,
    depth: 0.5 + Math.random() * 0.5,
  };
}

function resolveLeafAlpha(depth: number, isDarkDull: boolean): number {
  const base = GLOBAL_ALPHA * depth;
  const boosted = isDarkDull ? base * 1.6 + 0.18 : base;
  return Math.min(isDarkDull ? 1 : 0.85, boosted);
}
